import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { useScenario } from "@/contexts/ScenarioContext";
import { useI18n } from "@/contexts/I18nContext";
import { Play, ChevronRight } from "lucide-react";
import { toast } from "sonner";

function fmtDuration(min) {
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h ${m}m`;
}

const SEV_STYLE = {
  Critical: "text-red-400 border-red-500/40 bg-red-500/10",
  Warning: "text-amber-400 border-amber-500/40 bg-amber-500/10",
};

export default function CriticalAlertsTable() {
  const { tenant } = useTenant();
  const { scenario } = useScenario();
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [running, setRunning] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!tenant || !scenario) return;
    api.get(`/dashboard/critical-alerts?tenant=${tenant.id}&scenario=${scenario.id}`).then(({ data }) => setRows(data));
  }, [tenant, scenario]);

  const handleAction = async (row) => {
    setRunning(row.id);
    try {
      const { data } = await api.post(`/playbooks/${row.playbook}/run?tenant=${tenant.id}`);
      toast.success(`Playbook executed`, {
        description: `${data.log.length} steps OK · ${row.event}`,
        action: { label: "View", onClick: () => navigate("/agents?tab=playbooks") },
      });
    } catch {
      toast.error("Playbook execution failed");
    } finally {
      setRunning(null);
    }
  };

  return (
    <div className="overflow-x-auto" data-testid="critical-alerts-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] uppercase font-mono text-slate-500 border-b border-slate-800">
            <th className="text-left px-5 py-3">{t("alerts.severity")}</th>
            <th className="text-left px-5 py-3">{t("alerts.unit")}</th>
            <th className="text-left px-5 py-3">{t("alerts.event")}</th>
            <th className="text-left px-5 py-3">{t("alerts.duration")}</th>
            <th className="text-right px-5 py-3">{t("alerts.action")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-slate-800/50 hover:bg-[#1E293B]/40" data-testid={`critical-row-${r.id}`}>
              <td className="px-5 py-3">
                <span className={`text-[10px] font-mono font-bold px-2 py-0.5 border ${SEV_STYLE[r.severity]}`}>
                  ● {r.severity.toUpperCase()}
                </span>
              </td>
              <td className="px-5 py-3">
                <div className="text-slate-100 font-semibold">{r.unit}</div>
                <div className="text-[10px] font-mono text-slate-500">{r.unit_id}</div>
              </td>
              <td className="px-5 py-3 text-slate-300">{r.event}</td>
              <td className="px-5 py-3 font-mono text-slate-400">{fmtDuration(r.duration_min)}</td>
              <td className="px-5 py-3 text-right">
                <button
                  data-testid={`alert-action-${r.id}`}
                  onClick={() => handleAction(r)}
                  disabled={running === r.id}
                  className="bg-[#1E3A8A] hover:bg-[#1D4ED8] disabled:opacity-50 text-white px-3 py-1.5 rounded-sm text-xs font-semibold inline-flex items-center gap-1.5 transition-colors"
                >
                  <Play className="w-3 h-3" />
                  {running === r.id ? t("alerts.running") : t("alerts.remediate")}
                  <ChevronRight className="w-3 h-3 opacity-60" />
                </button>
              </td>
            </tr>
          ))}
          {!rows.length && (
            <tr><td colSpan={5} className="px-5 py-10 text-center text-xs text-slate-500 font-mono">{t("alerts.none")}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
